import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, Trash2, FileText, RefreshCw, Lock, Eye, EyeOff, Award, Users } from "lucide-react";
import { StaffAccess } from "@/components/admin/StaffAccess";
import { RequestsTable } from "@/components/admin/RequestsTable";
import { InterviewBox } from "@/components/admin/interviews/InterviewBox";
import { InterviewsPanel } from "@/components/admin/interviews/InterviewsPanel";
import { toast } from "sonner";
import { http, errText } from "@/lib/api";
import { Wordmark } from "@/components/site/Logo";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const STATUSES = ["new", "reviewing", "contacted", "archived"];

const SensitiveBox = ({ id, count }) => {
  const [vals, setVals] = useState(null);
  if (!count) return null;
  const reveal = async () => {
    if (vals) { setVals(null); return; }
    try { const r = await http.get(`/admin/applications/${id}/sensitive`); setVals(Array.isArray(r.data?.fields) ? r.data.fields : []); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="p-4 rounded-2xl border-2 border-[#CDA34F] bg-[#FFFDF6] space-y-3" data-testid="admin-sensitive-box">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-bold text-[#154F49] flex items-center gap-2"><Lock className="w-4 h-4" /> Encrypted fields ({count})</p>
        <button type="button" onClick={reveal} className="btn-ghost" data-testid="admin-reveal-sensitive">{vals ? <><EyeOff className="w-4 h-4" /> Hide</> : <><Eye className="w-4 h-4" /> Reveal</>}</button>
      </div>
      {vals && <dl className="grid sm:grid-cols-2 gap-2 text-sm">{vals.map((f) => <div key={f.key}><dt className="text-xs text-[#465F57]">{f.label}</dt><dd className="font-mono font-semibold text-[#1A2E2B]" data-testid={`sensitive-${f.key}`}>{f.value}</dd></div>)}</dl>}
      <p className="text-[11px] text-[#465F57]">Each reveal is logged with your staff email.</p>
    </div>
  );
};

const Detail = ({ id, onChanged, onClose }) => {
  const [app, setApp] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    setApp(null);
    http.get(`/admin/applications/${id}`).then((r) => setApp(r.data)).catch((e) => toast.error(errText(e)));
    return undefined;
  }, [id]);
  const setStatus = async (status) => {
    try { await http.patch(`/admin/applications/${id}`, { status }); setApp((a) => ({ ...a, status })); onChanged(); }
    catch (e) { toast.error(errText(e)); }
  };
  const download = async (url, name) => {
    try {
      const r = await http.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
    } catch (e) { toast.error("Could not download file."); }
  };
  const resume = () => download(`/admin/applications/${id}/resume`, app.resume_name);
  const remove = async () => {
    if (!window.confirm("Delete this application and its resume permanently?")) return;
    try { await http.delete(`/admin/applications/${id}`); toast.success("Deleted."); onChanged(); onClose(); }
    catch (e) { toast.error(errText(e)); }
  };
  if (!app) return <p className="p-6 text-[#465F57]">Loading…</p>;
  return (
    <div className="space-y-6 pb-10" data-testid="admin-application-detail">
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s} type="button" onClick={() => setStatus(s)} data-testid={`status-btn-${s}`} className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize border ${app.status === s ? "bg-[#154F49] text-[#FBF8F1] border-[#154F49]" : "border-[#154F49]/30 text-[#154F49]"}`}>{s}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => download(`/admin/applications/${id}/pdf`, `Employment-Application-${app.reference || id}.pdf`)} className="btn-primary" data-testid="download-pdf-btn"><FileText className="w-4 h-4" /> Completed application PDF</button>
        {app.resume_name && <button type="button" onClick={resume} className="btn-gold" data-testid="download-resume-btn"><FileText className="w-4 h-4" /> {app.resume_name}</button>}
        {(app.certificates || []).map((c) => (
          <button key={c.id} type="button" onClick={() => download(`/admin/applications/${id}/files/${c.id}`, c.name)} className="btn-ghost" data-testid={`download-cert-${c.id}`}><Award className="w-4 h-4" /> {c.kind === "supporting" ? "Supporting: " : ""}{c.name}</button>
        ))}
        <a href={`mailto:${app.email}`} className="btn-ghost">Email applicant</a>
        <button type="button" onClick={remove} className="btn-danger" data-testid="delete-application-btn"><Trash2 className="w-4 h-4" /> Delete</button>
      </div>
      <SensitiveBox id={id} count={app.sensitive_fields?.length || 0} />
      <InterviewBox appId={id} />
      <div className="flex flex-wrap gap-2 text-xs" data-testid="admin-notification-status">
        <span className="px-2.5 py-1 rounded-full bg-[#EDF1E6] text-[#154F49] font-bold">{app.reference || `ELV-${app.id.slice(-6).toUpperCase()}`}</span>
        {[["confirmation_email", "Applicant email"], ["sms", "Staff SMS"], ["telegram", "Telegram"]].map(([k, l]) => (
          <span key={k} className="px-2.5 py-1 rounded-full border border-[#154F49]/25 text-[#1A2E2B]">{l}: <strong>{(app.notifications?.[k] || "—").replace("_", " ")}</strong></span>
        ))}
      </div>
      {Object.entries(app.sections || {}).map(([sec, fields]) => (
        <div key={sec}>
          <p className="text-xs font-bold uppercase tracking-wider text-[#9B742A] border-b border-[#154F49]/20 pb-1 mb-2">{sec}</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {Object.entries(fields).filter(([, v]) => v !== "N/A").map(([k, v]) => (
              <div key={k}><dt className="text-xs text-[#465F57]">{k}</dt><dd className={`text-[#1A2E2B] ${v === "N/A" ? "text-[#5C706B]" : "font-medium"} break-words`}>{String(v)}</dd></div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const [sel, setSel] = useState(() => {
    const id = new URLSearchParams(window.location.search).get("application") || sessionStorage.getItem("elevion_open_app");
    sessionStorage.removeItem("elevion_open_app");
    return /^[a-f0-9]{24}$/.test(id || "") ? id : null;
  });
  const [staffOpen, setStaffOpen] = useState(false);
  const [view, setView] = useState("apps");
  const load = useCallback(() => http.get("/admin/applications").then((r) => setApps(Array.isArray(r.data) ? r.data : [])).catch(() => {}), []);
  useEffect(() => {
    http.get("/auth/me").then((r) => { setUser(r.data); load(); }).catch(() => { if (sel) sessionStorage.setItem("elevion_open_app", sel); nav("/admin/login"); });
    return undefined;
  }, [nav, load]); // eslint-disable-line react-hooks/exhaustive-deps
  const logout = async () => { await http.post("/auth/logout").catch(() => {}); nav("/admin/login"); };
  const exportCsv = () => {
    const rows = [["Submitted", "Name", "Email", "Phone", "Positions", "Status", "Resume"], ...apps.map((a) => [a.created_at, a.full_name, a.email, a.phone, a.positions, a.status, a.resume_name || ""])];
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "elevion-applications.csv"; a.click();
  };
  if (!user) return <div className="min-h-screen bg-[#FBF8F1]" />;
  const current = apps.find((a) => a.id === sel);
  return (
    <div className="min-h-screen bg-[#FBF8F1] font-sans" data-testid="admin-dashboard">
      <header className="bg-[#FBF8F1] border-b border-[#154F49]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-3">
          <a href="/" aria-label="Website"><Wordmark /></a>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-[#465F57]">{user.email}</span>
            <button type="button" onClick={logout} className="btn-ghost" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9B742A]">Hiring portal</p>
            <h1 className="font-serif text-5xl text-[#154F49]">{view === "apps" ? <>Applications <span className="text-[#CDA34F]" data-testid="admin-app-count">{apps.length}</span></> : view === "interviews" ? "Interviews" : "Requests"}</h1>
            <div role="tablist" className="mt-3 inline-flex flex-wrap gap-1 p-1 rounded-3xl sm:rounded-full bg-white border border-[#154F49]/15">
              {[["apps", "Job applications"], ["interviews", "Interviews"], ["requests", "Care & staffing requests"]].map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`admin-view-${k}`} className={`px-4 py-2 rounded-full text-sm font-semibold ${view === k ? "bg-[#154F49] text-[#FBF8F1]" : "text-[#154F49]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 [&>button]:whitespace-nowrap">
            <button type="button" onClick={() => setStaffOpen(true)} className="btn-ghost" data-testid="admin-staff-btn"><Users className="w-4 h-4" /> Staff access</button>
            <button type="button" onClick={load} className="btn-ghost" data-testid="admin-refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <button type="button" onClick={exportCsv} disabled={!apps.length} className="btn-primary disabled:opacity-50" data-testid="admin-export-btn"><Download className="w-4 h-4" /> Export CSV</button>
          </div>
        </div>
        {view === "interviews" ? <InterviewsPanel /> : view === "requests" ? <RequestsTable /> : (
        <div className="bg-white rounded-3xl border border-[#154F49]/15 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="admin-applications-table">
            <thead className="bg-[#EDF1E6] text-left text-xs uppercase tracking-wider text-[#154F49]">
              <tr><th className="p-4">Submitted</th><th className="p-4">Applicant</th><th className="p-4">Positions</th><th className="p-4">Files</th><th className="p-4">Status</th></tr>
            </thead>
            <tbody>
              {apps.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[#465F57]">No applications yet.</td></tr>}
              {apps.map((a) => (
                <tr key={a.id} onClick={() => setSel(a.id)} className="border-t border-[#154F49]/10 hover:bg-[#FBF8F1] cursor-pointer" data-testid={`admin-row-${a.id}`}>
                  <td className="p-4 whitespace-nowrap text-[#465F57]">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="p-4"><button type="button" className="text-left font-semibold text-[#154F49] underline-offset-4 hover:underline">{a.full_name}</button><p className="text-xs text-[#465F57]">{a.email} · {a.phone}</p></td>
                  <td className="p-4 text-[#1A2E2B] max-w-xs">{a.positions}</td>
                  <td className="p-4">{a.resume_name || a.certificates?.length ? <span className="inline-flex items-center gap-1 text-[#154F49]"><FileText className="w-4 h-4" aria-label="Files attached" />{(a.resume_name ? 1 : 0) + (a.certificates?.length || 0)}</span> : "—"}</td>
                  <td className="p-4"><span className="px-2.5 py-1 rounded-full bg-[#EDF1E6] text-[#154F49] text-xs font-bold capitalize">{a.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </main>
      <StaffAccess open={staffOpen} onOpenChange={setStaffOpen} />
      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto bg-[#FBF8F1]">
          <SheetHeader className="mb-4">
            <SheetTitle className="font-serif text-3xl text-[#154F49]">{current?.full_name || "Application"}</SheetTitle>
            <SheetDescription>{current?.email} · {current?.phone}</SheetDescription>
          </SheetHeader>
          {sel && <Detail id={sel} onChanged={load} onClose={() => setSel(null)} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
